<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import type { WebRTCTalkSignal } from '../types/webrtc-events';
	import { PttUiState } from '../types/ptt-ui-state';
	import { load_image_from } from 'src/helpers/interface_helpers';
	import { camera_previews } from 'src/stores/features/cameras/previews';
	import { buildCloudMediaUrl } from 'src/services/cam-cloud/recordings';
	import { toast } from '@Shelly/shelly-design-system';
	import WebRtcInfo from './Debug/WebRTCInfo.svelte';
	import { WebRTCClient } from '../helpers/webrtc';
	import { TALK_CONFIRMATION_TIMEOUT_MS } from '../helpers/webrtc-config';

	export let deviceId: string = null;
	export let showControls: boolean = true;
	export let streamId: number = 0;
	export let existingMediaStream: MediaStream = null; // MediaStream is already playing from another component
	export let currentStream: MediaStream = null; // Expose the currently playing stream to the parent component
	export let objectFit: string = 'cover';

	// Whether to show debug info below the video
	export let showInfo: boolean = false;

	// Exposed state for parent to bind and pass to VideoContainer
	export let isLoading: boolean = true;
	export let errorMessage: { heading: string; message: string } = { heading: '', message: '' };
	export let pttState: PttUiState = PttUiState.UNAVAILABLE;

	let videoElement: HTMLVideoElement = null;
	let client: WebRTCClient | null = null;
	let retryTimeout: ReturnType<typeof setTimeout> | null = null;
	let talkConfirmTimeout: ReturnType<typeof setTimeout> | null = null;
	let destroyed = false;

	$: startStream(deviceId);

	// Build authenticated poster URL
	let imagePoster = load_image_from('images/device_images/logo_white_S.png');
	$: {
		const baseUrl = $camera_previews?.[deviceId]?.large;
		if (baseUrl) {
			buildCloudMediaUrl(baseUrl).then((url) => (imagePoster = url));
		} else {
			imagePoster = load_image_from('images/device_images/logo_white_S.png');
		}
	}

	const handleBeforeUnload = () => {
		stopStream();
	};

	onMount(() => {
		if (currentStream && videoElement && !videoElement.srcObject) {
			playStream();
		}
		window.addEventListener('beforeunload', handleBeforeUnload);
	});

	const clearError = () => {
		errorMessage = { heading: '', message: '' };
	};

	const handleError = (title: string, message?: string, retryDelay: number = 5000) => {
		if (destroyed) return;

		errorMessage = {
			heading: title || 'Error',
			message: message || '',
		};

		stopStream(true);
		retryConnection(retryDelay);

		isLoading = true;
	};

	const handleConnectionStateEvent = (
		eventName: string,
		pc: RTCPeerConnection,
		event: Event | string,
	) => {
		if (eventName === 'start_error') {
			const message = event.toString();
			if (message.includes('Awaiting cloud connection')) {
				clearError();
				retryConnection(1000);
				return;
			}
			handleError('Stream Error', message, 1000);
			return;
		}

		if (eventName === 'onicegatheringstatechange') return;

		console.info(
			`[WebRTC View] State changed (${deviceId}):`,
			`connection=${pc.connectionState}, ice=${pc.iceConnectionState},`,
			`signaling=${pc.signalingState}, event=${eventName}`,
		);

		if (pc.iceConnectionState === 'disconnected') {
			console.warn('[WebRTC View] ICE Disconnected (' + deviceId + '). Reconnecting...');
			handleError('Stream Disconnected', 'Reconnecting...', 100);
		}
	};

	const clearTalkConfirmTimeout = () => {
		if (talkConfirmTimeout) {
			clearTimeout(talkConfirmTimeout);
			talkConfirmTimeout = null;
		}
	};

	const clearRetryTimeout = () => {
		if (retryTimeout) {
			clearTimeout(retryTimeout);
			retryTimeout = null;
		}
	};

	/**
	 * Reduces raw `WebRTCTalkSignal`s from the client into the local
	 * `pttState` machine:
	 * - `ready`     → `'ready'` (unless mid-talk: a `granted`/`requesting`
	 *                  state is authoritative over a late channel-open).
	 * - `granted`   → `'granted'`.
	 * - `released` → `'ready'` (transient; we never rest on `'released'`).
	 * - `closed`   → `'closed'` (talkback no longer usable).
	 * The confirmation timeout is cleared on every camera-confirmed signal.
	 */
	const handleTalkSignal = (signal: WebRTCTalkSignal) => {
		if (signal === 'ready') {
			if (pttState !== PttUiState.GRANTED && pttState !== PttUiState.REQUESTING) {
				pttState = PttUiState.READY;
			}
			return;
		}
		clearTalkConfirmTimeout();
		if (signal === 'granted') {
			pttState = PttUiState.GRANTED;
		} else if (signal === 'released') {
			pttState = PttUiState.READY;
		} else {
			pttState = PttUiState.CLOSED;
		}
	};

	const handleTrackEvent = (event: RTCTrackEvent) => {
		if (event?.track?.kind === 'audio') {
			// Attach the camera's audio track to the existing stream so the
			// user can hear the camera. The `<video>` stays `muted` until a
			// dedicated capability/UX flips it; see follow-ups in the plan.
			if (currentStream) {
				if (currentStream.getAudioTracks().length === 0) {
					currentStream.addTrack(event.track);
				}
			} else if (event.streams?.length) {
				currentStream = event.streams[0];
				playStream();
			} else {
				currentStream = new MediaStream([event.track]);
				playStream();
			}
			return;
		}

		if (event?.track?.kind !== 'video') {
			return;
		}

		if (!event?.streams?.length) {
			handleError('Live Stream Error', 'No stream to play');
			return;
		}

		currentStream = event.streams[0];
		playStream();
	};

	const playStream = async () => {
		if (!videoElement) {
			return;
		}
		isLoading = true;
		videoElement.srcObject = currentStream;
		videoElement.muted = true;
		videoElement.onplaying = () => {
			isLoading = false;
		};
		videoElement.oncanplay = () => {
			// These media callbacks fire asynchronously; the `bind:this` is nulled
			// when the component is torn down mid-load, so guard the whole chain.
			videoElement?.play()?.catch((e) => {
				isLoading = false;
				handleError('Video Play Error', 'Unable to play the video stream.');
				console.warn('[WebRTC View] Video play error:', e);
			});
		};

		clearError();
	};

	const retryConnection = (delay = 5000) => {
		if (destroyed) return;
		isLoading = true;
		retryTimeout = setTimeout(() => {
			if (destroyed) return;
			startStream(deviceId, true);
		}, delay);
	};

	/**
	 * Tears down the active client, mic and media tracks.
	 *
	 * @param isReloading - True when called from the (re)connect path, which owns
	 * `isLoading` itself. Clearing loading here mid-reconnect (while a
	 * 'Stream Disconnected' heading is still set) would make `computeStreamState`
	 * flash the error placeholder over the reconnecting overlay. A terminal stop
	 * (default) clears loading.
	 */
	const stopStream = async (isReloading = false) => {
		clearRetryTimeout();
		clearTalkConfirmTimeout();
		if (!isReloading) {
			isLoading = false;
		}
		pttState = PttUiState.UNAVAILABLE;

		// Capture and detach the current client
		const previousClient = client;
		client = null;

		// Release the mic (and the OS indicator) before tearing down the PC.
		try {
			await previousClient?.stopTalk?.();
		} catch (e) {
			console.warn('[WebRTC View] stopTalk during stopStream failed:', e);
		}

		previousClient?.stop();

		if (existingMediaStream && currentStream === existingMediaStream) {
			if (videoElement) {
				videoElement.srcObject = null;
			}
			currentStream = null;
			return;
		}

		if (currentStream?.getTracks().length > 0) {
			currentStream.getTracks().forEach((track) => {
				track.stop();
				currentStream.removeTrack(track);
			});
			if (videoElement) {
				videoElement.srcObject = null;
			}
			currentStream = null;
		}
	};

	export async function requestTalk(desired: boolean): Promise<void> {
		if (!client || pttState === PttUiState.REQUESTING) return;
		if (desired === (pttState === PttUiState.GRANTED)) return;
		// Starting the floor is only valid from a usable, idle channel.
		if (desired && pttState !== PttUiState.READY) return;
		pttState = PttUiState.REQUESTING;
		clearTalkConfirmTimeout();

		// Surface a hint when the OS-level mic prompt is about to appear. The
		// browser doesn't fire an event for "prompt is currently visible", but
		// a `'prompt'` permission state means the next getUserMedia call will
		// trigger one. Cosmetic only — getUserMedia's promise is the source
		// of truth for granted/denied.
		if (desired && navigator.permissions?.query) {
			try {
				const status = await navigator.permissions.query({
					name: 'microphone' as PermissionName,
				});
				if (status.state === 'prompt') {
					toast.open({
						type: 'warning',
						title: 'Allow microphone access to use Push to talk',
					});
				}
			} catch {
				/* some browsers don't accept 'microphone' as a queryable permission */
			}
		}

		try {
			if (desired) {
				await client.startTalk();
			} else {
				await client.stopTalk();
			}
			// Mic acquired + DC `on`/`off` frame sent. Only now arm the
			// confirmation timer — it bounds the camera's echo, not the
			// permission-prompt window.
			talkConfirmTimeout = setTimeout(() => {
				console.warn(
					`[WebRTC View] PTT confirmation timed out (desired=${desired}); forcing local state.`,
				);
				pttState = desired ? PttUiState.GRANTED : PttUiState.READY;
				talkConfirmTimeout = null;
				toast.open({
					type: 'warning',
					title: 'Push to talk could not be confirmed by camera',
				});
			}, TALK_CONFIRMATION_TIMEOUT_MS);
		} catch (e) {
			console.warn('[WebRTC View] requestTalk failed:', e);
			clearTalkConfirmTimeout();
			toast.open({ type: 'danger', title: 'Microphone access denied' });
			// The channel is still open; fall back to idle-but-usable.
			pttState = PttUiState.READY;
		}
	}

	const startStream = async (device_id: string, is_retry: boolean = false) => {
		if (destroyed) return;

		if (!device_id) {
			errorMessage = { heading: 'Could not find device', message: '' };
			isLoading = false;
			return;
		}

		if (existingMediaStream) {
			currentStream = existingMediaStream;
			isLoading = true;

			playStream();
			return;
		}

		await stopStream(true);
		if (destroyed) return;
		if (!is_retry) {
			clearError();
		}
		isLoading = true;

		client = new WebRTCClient(
			device_id,
			{
				onTrack: handleTrackEvent,
				onConnectionState: handleConnectionStateEvent,
				onTalkState: handleTalkSignal,
			},
			streamId,
		);
	};

	onDestroy(() => {
		destroyed = true;
		stopStream();
		window.removeEventListener('beforeunload', handleBeforeUnload);
	});

	// Expose retry function for parent to call
	export function retry() {
		startStream(deviceId);
	}

	/**
	 * Tears down the active stream (terminal stop -> `client.stop()` ->
	 * `streamer.stopstream`).
	 *
	 * Exposed for parents that keep this component mounted while hidden: the
	 * drawer/sidepanel slides it off-screen instead of unmounting, so
	 * `onDestroy` never fires on close. Close-only; the player owns its own
	 * start (on mount and from other parents).
	 */
	export function stop() {
		stopStream();
	}
</script>

<video
	poster={imagePoster}
	bind:this={videoElement}
	autoPlay
	playsInline
	muted
	controls={showControls && !isLoading}
	style={`object-fit: ${objectFit};`}
/>
{#if showInfo}
	<div class="width-full">
		<WebRtcInfo {deviceId} webRTChandler={client} streamMediaStream={currentStream} />
	</div>
{/if}

<style>
	video {
		display: block;
		width: 100%;
		aspect-ratio: 16 / 9;
		background-color: var(--color-black, #000);
	}
</style>
